import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js/mobile';
import examples from 'libphonenumber-js/mobile/examples';
import { LOCALE_TAGS, type Locale } from '@360face/domain';

export type { CountryCode };

/**
 * Countries shown first in the picker, owner's order 14.10.2026, based on the phone codes of
 * the studio's 1 092 clients in Altegio (PT 835, GB 52, +1 36, +7 23, UA 15, FR/DE/IE 11, NL 10, ES 9).
 * US and RU share their calling code with Canada and Kazakhstan: one entry each, both names.
 */
export const TOP_COUNTRIES: readonly CountryCode[] = ['PT', 'GB', 'US', 'UA', 'FR', 'DE', 'IE', 'RU', 'NL', 'ES'];
const SHARED_NAMES: Partial<Record<CountryCode, CountryCode>> = { US: 'CA', RU: 'KZ' };
export const DEFAULT_COUNTRY: CountryCode = 'PT';

export interface CountryOption {
  code: CountryCode;
  name: string;
  dial: string;
  flag: string;
}

/** Emoji flag from an ISO code (regional indicator letters). */
export function flagOf(code: string): string {
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

export function countryOptions(locale: Locale): CountryOption[] {
  let names: Intl.DisplayNames | undefined;
  try {
    names = new Intl.DisplayNames([LOCALE_TAGS[locale]], { type: 'region' });
  } catch {
    names = undefined;
  }
  const all = getCountries().map((code) => ({
    code,
    name: names?.of(code) ?? code,
    dial: `+${getCountryCallingCode(code)}`,
    flag: flagOf(code),
  }));
  const top = TOP_COUNTRIES.map((c) => all.find((x) => x.code === c)!)
    .filter(Boolean)
    .map((x) => {
      const also = SHARED_NAMES[x.code];
      return also ? { ...x, name: `${x.name} / ${names?.of(also) ?? also}` } : x;
    });
  const rest = all
    .filter((x) => !TOP_COUNTRIES.includes(x.code))
    .sort((a, b) => a.name.localeCompare(b.name, LOCALE_TAGS[locale]));
  return [...top, ...rest];
}

/** Search by country name, ISO code or dialling code ("moz", "MZ", "+258", "258"). */
export function searchCountries(list: CountryOption[], query: string): CountryOption[] {
  const q = query.trim().toLowerCase().replace(/^\+/, '');
  if (!q) return list;
  return list.filter(
    (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q || c.dial.slice(1).startsWith(q),
  );
}

export function dialCode(country: CountryCode): string {
  return `+${getCountryCallingCode(country)}`;
}

/** Only digits, at most 15 (E.164 limit). */
export function cleanNational(input: string): string {
  return input.replace(/\D/g, '').slice(0, 15);
}

/**
 * The number as people write it in that country, while typing: PT "912 345 678",
 * US "(201) 555-0123", GB "07400 123456", FR "06 12 34 56 78".
 */
export function formatNational(country: CountryCode, digits: string): string {
  const clean = cleanNational(digits);
  return clean ? new AsYouType(country).input(clean) : '';
}

/** A sample mobile number in the national format, used as the placeholder. */
export function examplePhone(country: CountryCode): string {
  return getExampleNumber(country, examples)?.formatNational() ?? '';
}

/**
 * E.164 (+351912345678) when it is a valid MOBILE number for the country (mobile metadata:
 * e.g. Portuguese mobiles start with 9), otherwise undefined. A national trunk 0 (UK 07…,
 * FR 06…) and a re-typed country code are accepted.
 */
export function toE164(country: CountryCode, national: string): string | undefined {
  const digits = cleanNational(national);
  if (digits.length < 4) return undefined;
  const parsed = parsePhoneNumberFromString(digits, country);
  if (parsed && parsed.isValid()) return parsed.number;
  // Same calling code, other country (Canada under US +1, Kazakhstan under RU +7).
  const shared = parsePhoneNumberFromString(`+${getCountryCallingCode(country)}${digits.replace(/^0/, '')}`);
  return shared && shared.isValid() ? shared.number : undefined;
}
